import React from 'react';
import { useAccountStore } from '@components/Stores/accountStore';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { useFormulasPrices } from '../../hooks/useFormulasPrices';
import { useExposure } from '@hooks/useExposure';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import { GENERAL_ID, useExposureStore } from '@components/Stores/exposureDataStore';
import SubMenu from './components/SubMenu';
import { ExposureChart } from './components/ExposureChart';
import { StrategyDetails } from './components/StrategyDetails';
import { ExposureSummary } from './components/ExposureSummary';
import { useUser } from '../../hooks/useUser';
import { Account, IFormulaToAccount } from '@sp-types/accountTypes';
import { isSuperAdmin } from '@sp-types/userType';
import { auth } from '../../sp-firebase/firebase';
import { useAppSession } from '../../contexts/AppSessionContext';
import { BufferManagement } from './components/BufferManagement';
import { BufferMonthDetail } from './components/BufferMonthDetail';
import { useExposureHistoryByMaterial } from '../../hooks/useExposureHistory';
import { IExposureHistory } from '../../utils/exposureUtils';
import { useLatestExposure } from '../../hooks/useLatestExposure';
import { LoadingSpinnerOverlay } from '../../components/UI-Elements/LoadingSpinnerOverlay';
import {
  LogisticsMonthData,
  TimeGranularity,
} from './components/StrategiesUtils';
import { useStrategyData } from './hooks/useStrategyData';
import { getUnitObj } from '../../utils/MassUnits/massUnitsUtils';
import { getFormulaToAccountById } from '../Dashboard/dashboardUtils';
import { LogisticsInvManagement } from './components/LogisticsInvManagement';
import { LogisticsMonthDetail } from './components/LogisticsMonthDetail';
import { CoverageManagement } from './components/CoverageManagement';
import { CoverageMonthDetail } from './components/CoverageMonthDetail';
import { HedgesDerivedMonthDetail } from './components/HedgesDerivedMonthDetail';
import { useWatchRules, useRuleTypes, useUpdateWatchRule, useCreateWatchRule, findRuleForTab, findAllRulesForTab, extractPolicyConfig, SUB_TAB_RULE_TYPES } from '../../hooks/useWatchRules';
import { AdminBufferTable } from './components/AdminBufferTable';
import { HedgesDeriving } from './components/HedgesDeriving';
import { useBaseHedgeMaterials } from '../../hooks/useBaseHedgeMaterials';
import { HedgePriceManagement } from './components/HedgePriceManagement';
import { HedgePriceMonthDetail } from './components/HedgePriceMonthDetail';
import { calculatePriceInsightData } from './components/StrategiesUtils';
import { BaseMaterialManagement, BaseMaterialMonthData } from './components/BaseMaterialManagement';
import { BaseMaterialMonthDetail } from './components/BaseMaterialMonthDetail';
import { M2mManagement, M2mAccountDetail } from './components/M2mManagement';
import { M2mDetail } from './components/M2mDetail';
import { useLatestM2m } from '../../hooks/useM2m';
import { TerminalCoverageTable } from './components/TerminalCoverageTable';
import { CommodexTable } from './components/CommodexTable';
import { HedgingRecommendations } from './components/HedgingRecommendations';

const subMenuItems: { key: string; label: string; subtitle?: string }[] = [
  { key: 'buffer', label: 'Buffer stock management', subtitle: 'Keep safety stock to absorb demand shocks' },
  { key: 'logistics', label: 'Logistic inventory management', subtitle: 'Track what moves in, out, and sits on the shelf' },
  { key: 'coverage', label: 'Coverage management', subtitle: 'Physical & financial coverage — do you need more hedging?' },
  { key: 'exposure', label: 'Exposure History', subtitle: 'Daily net exposure trend with risk zone tracking' },
  { key: 'hedges-deriving', label: 'Hedges Deriving', subtitle: 'See how base-material hedges cascade to derivatives' },
  { key: 'hedge-price', label: 'Price Insight', subtitle: 'Weighted avg cost vs spot — holdings & demand coverage' },
  { key: 'base-material', label: 'Base Material View', subtitle: 'Unified view across all derived materials' },
  { key: 'm2m', label: 'Mark to Market', subtitle: 'Daily P&L, margin health & price sensitivity' },
  { key: 'terminal-coverage', label: 'Terminal Coverage', subtitle: 'Hedge coverage by delivery terminal — demand vs. exchange positions' },
  { key: 'commodex-table', label: 'Commodex Table', subtitle: 'Full demand, orders, inventory & coverage table by material and terminal' },
  { key: 'hedging-recommendations', label: 'Hedging Recommendations', subtitle: 'Policy-driven hedge actions — what to do, per period' },
];

export const Strategies: React.FC = () => {
  const { account, formulaColorDictionary: colorsMap } = useAccountStore();
  const { data: user } = useUser();
  const { user: sessionUser } = useAppSession();
  const accountId = sessionUser?.accountId || '';
  // Disabled: we don't need Firebase account data anymore, using Postgres
  // const { data: accountData, refetch: refetchAccount } = useUserAccount(true);
  
  const {
    netExposure,
    currentSelectedFormulaId: materialId,
    setCurrentSelectedFormulaId: setMaterialId,
  } = useExposureStore();
  
  const getAccountMaterial = (acc: Account): IFormulaToAccount | undefined =>
    materialId ? getFormulaToAccountById(acc, materialId) : undefined;

  React.useEffect(() => {
    if (user && user.isDebug && !subMenuItems.find((item) => item.key.startsWith('admin-'))) {
      // subMenuItems.push({ key: 'admin-buffer', label: 'Table buffer' });
      // subMenuItems.push({ key: 'admin-logistic', label: 'Table logistic' });
      subMenuItems.push({ key: 'admin-inv', label: 'Deltas tab' });
      subMenuItems.push({ key: 'admin-pivot', label: 'Pivot tab' });
    }
  }, [user]);

  const { materialsMap } = useMaterialsStore();
  const { data: formulasPricesData } = useFormulasPrices();
  const pricesMap = React.useMemo(() => {
    if (!formulasPricesData?.pricesMap) return {};
    // Convert to simple map: formulaCode -> price in base currency
    const map: Record<string, number> = {};
    Object.entries(formulasPricesData.pricesMap).forEach(([code, info]) => {
      map[code] = info.price;
    });
    return map;
  }, [formulasPricesData]);
  
  // Fetch base hedge materials for hedges-deriving tab
  const numericAccountId = React.useMemo(() => {
    if (!accountId) return undefined;
    const id = typeof accountId === 'string' ? parseInt(accountId) : accountId;
    console.log('[Strategy] Numeric account ID from sessionUser:', { accountId, numericId: id });
    return id;
  }, [accountId]);
  
  const { data: baseHedgeMaterials } = useBaseHedgeMaterials(numericAccountId);
  
  React.useEffect(() => {
    console.log('[Strategy] Base hedge materials fetched:', {
      accountId: numericAccountId,
      baseHedgeMaterials,
      count: baseHedgeMaterials?.length || 0,
    });
  }, [baseHedgeMaterials, numericAccountId]);
  
  // Get set of base material formula codes
  const baseMaterialCodes = React.useMemo(() => {
    if (!baseHedgeMaterials) return new Set<string>();
    const codes = new Set(baseHedgeMaterials.map(bm => bm.formula_code));
    console.log('[Strategy] Base material codes:', Array.from(codes));
    return codes;
  }, [baseHedgeMaterials]);
  const [logisticsPivot, setLogisticsPivot] = React.useState<LogisticsMonthData[]>([]);
  const [activeSub, setActiveSub] = React.useState(subMenuItems[0].key);
  const isSuperAdminUser = isSuperAdmin(user);
  const [timeGranularity, setTimeGranularity] = React.useState<TimeGranularity>('Month');
  const [logisticsTimeGranularity, setLogisticsTimeGranularity] = React.useState<TimeGranularity>('Month');
  const [priceTimeGranularity, setPriceTimeGranularity] = React.useState<TimeGranularity>('Full');

  // Reset time granularity when switching policy tabs so chart toggle and data stay in sync
  React.useEffect(() => {
    setTimeGranularity('Month');
    setLogisticsTimeGranularity('Month');
    setPriceTimeGranularity('Full');
  }, [activeSub]);
  const [selectedMonthIndex, setSelectedMonthIndex] = React.useState<number>(0);
  const [selectedExposureSnapshot, setSelectedExposureSnapshot] = React.useState<IExposureHistory | null>(null);
  const [terminals, setTerminals] = React.useState<number[]>([]);
  const [hedgesDerivedData, setHedgesDerivedData] = React.useState<any[]>([]);
  const [derivedMaterials, setDerivedMaterials] = React.useState<any[]>([]);
  const [formulaColorDictionary, setFormulaColorDictionary] = React.useState<Record<string, string>>({});
  const [baseMaterialData, setBaseMaterialData] = React.useState<BaseMaterialMonthData[]>([]);
  const [baseDerivedMaterials, setBaseDerivedMaterials] = React.useState<Array<{ formula_code: string; formula_name: string; price_ratio: number }>>([]);

  // M2M data
  const { data: m2mSnapshot, isLoading: isM2mLoading } = useLatestM2m();

  // ─── Watch Rules ─────────────────────────────────────────────────
  const { data: watchRules = [] } = useWatchRules(accountId);
  const { data: ruleTypes = [] } = useRuleTypes();
  const updateWatchRuleMutation = useUpdateWatchRule(accountId);
  const createWatchRuleMutation = useCreateWatchRule(accountId);

  // Resolve the numeric formula_id for the current materialId (formula code)
  const numericFormulaId = React.useMemo(() => {
    if (!materialId || materialId === GENERAL_ID) return null;
    const am = getAccountMaterial(account);
    return am?.id ? Number(am.id) : null;
  }, [materialId, account]);

  // Find the active watch rule + its metadata for the current sub-tab
  const activeWatchRule = React.useMemo(
    () => findRuleForTab(watchRules, activeSub, numericFormulaId),
    [watchRules, activeSub, numericFormulaId],
  );
  // All rules for this tab (multi-rule support for tabs like hedge-price)
  const allActiveRules = React.useMemo(
    () => findAllRulesForTab(watchRules, activeSub, numericFormulaId),
    [watchRules, activeSub, numericFormulaId],
  );
  // Resolve meta even without an active rule (for "Configure" empty state)
  const activeRuleMeta = React.useMemo(() => {
    if (activeWatchRule) return ruleTypes.find(rt => rt.ruleType === activeWatchRule.rule_type);
    const tabRuleTypes = SUB_TAB_RULE_TYPES[activeSub];
    if (tabRuleTypes?.length) return ruleTypes.find(rt => tabRuleTypes.includes(rt.ruleType));
    return undefined;
  }, [activeWatchRule, ruleTypes, activeSub]);

  const handleWatchRuleSave = React.useCallback(async (ruleId: number, condition: Record<string, any>) => {
    await updateWatchRuleMutation.mutateAsync({ ruleId, updates: { condition } });
    console.log('[Strategy] Watch rule condition saved:', { ruleId, condition });
  }, [updateWatchRuleMutation]);

  const handleCreateRule = React.useCallback(async (ruleTypeOverride?: string) => {
    const meta = ruleTypeOverride
      ? ruleTypes.find(rt => rt.ruleType === ruleTypeOverride)
      : activeRuleMeta;
    if (!meta) return;
    try {
      const newRule: any = {
        name: meta.label,
        category: meta.category,
        rule_type: meta.ruleType,
        formula_id: numericFormulaId,
        condition: meta.defaultCondition,
        enabled: true,
      };
      await createWatchRuleMutation.mutateAsync(newRule);
      console.log('[Strategy] Created rule:', meta.ruleType);
    } catch (err) {
      console.error('[Strategy] Failed to create rule:', err);
    }
  }, [activeRuleMeta, ruleTypes, numericFormulaId, createWatchRuleMutation]);

  // Single-source policy config derived from watch rules
  const policyConfig = React.useMemo(() => {
    if (!watchRules.length) return undefined;
    return extractPolicyConfig(watchRules, numericFormulaId);
  }, [watchRules, numericFormulaId]);

  const { refetch: fetchExposure } = useExposure(accountId);
  const [startDate] = React.useState<Date>(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 2);
    return d;
  });

  // Centralized data fetching and calculation hook
  const {
    supplyPositions,
    demandPositions,
    hedgePositions,
    isDemandLoading,
    isSupplyLoading,
    bufferData,
    coverageData,
    logisticsData,
    refetchHedgePositions,
    BUFFER_DEFAULT: bufferDefault,
  } = useStrategyData({
    materialId,
    account,
    activeSub,
    timeGranularity,
    logisticsTimeGranularity,
    terminals,
    policyConfig,
  });

  // Fetch exposure history for the selected material (only when on Exposure tab)
  const shouldFetchHistory = activeSub === 'exposure';
  const {
    data: exposureHistory,
    isLoading: isLoadingHistory,
    error: historyError,
    refetch: refetchHistory,
  } = useExposureHistoryByMaterial(accountId, materialId || '', startDate, shouldFetchHistory && !!accountId && !!materialId);

  // Fetch latest snapshots for last-calculated time and category breakdown
  const { data: latestSnapshots, refetch: refetchLatest } = useLatestExposure(accountId, shouldFetchHistory);
  const latestSnapshot = React.useMemo(() => {
    if (!latestSnapshots || !materialId) return undefined;
    if (materialId === GENERAL_ID) return latestSnapshots.find(s => s.formulaId === null);
    return latestSnapshots.find(s => s.formulaCode === materialId);
  }, [latestSnapshots, materialId]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      fetchExposure();
      refetchHedgePositions();
      setLoading(false);
    };
    if (accountId) {
      fetchAll();
    }
  }, [accountId]);

  React.useEffect(() => {
    if (materialId && accountId && activeSub === 'exposure') {
      refetchHistory();
    }
  }, [materialId, accountId, activeSub, refetchHistory]);

  React.useEffect(() => {
    if (historyError) {
      console.error('[Strategy] Error loading exposure history:', historyError);
    }
  }, [exposureHistory, materialId, historyError]);

  // Which formulas have position data (cheap — just unique codes from loaded positions)
  const formulasWithData = React.useMemo(() => {
    const codes = new Set<string>();
    for (const p of supplyPositions) if (p.formula_code) codes.add(p.formula_code);
    for (const p of demandPositions) if (p.formula_code) codes.add(p.formula_code);
    for (const p of hedgePositions) if (p.formula_code) codes.add(p.formula_code);
    return codes;
  }, [supplyPositions, demandPositions, hedgePositions]);

  // M2M formula codes (from snapshot groups)
  const m2mFormulaCodes = React.useMemo(() => {
    if (!m2mSnapshot) return new Set<string>();
    return new Set(m2mSnapshot.groups.map(g => g.formulaCode).filter(Boolean) as string[]);
  }, [m2mSnapshot]);

  // Per-policy card eligibility — modular: add one line per policy with special rules
  const isCardEnabled = React.useCallback((formulaCode: string): boolean => {
    if (formulaCode === GENERAL_ID) return activeSub === 'exposure' || activeSub === 'm2m';
    if (activeSub === 'exposure') {
      // Ratio formulas have no meaningful exposure — disable
      return materialsMap[formulaCode]?.contractType !== 'ratio';
    }
    if (activeSub === 'hedges-deriving' || activeSub === 'base-material' || activeSub === 'terminal-coverage' || activeSub === 'commodex-table') {
      return baseMaterialCodes.has(formulaCode);
    }
    if (activeSub === 'm2m') return m2mFormulaCodes.has(formulaCode);
    return formulasWithData.has(formulaCode);
  }, [activeSub, baseMaterialCodes, formulasWithData, m2mFormulaCodes, materialsMap]);

  // Auto-select first enabled card on mount and when switching policies
  React.useEffect(() => {
    if (!account.formulasToAccount?.length) return;
    // If current selection is disabled or missing, pick first enabled
    if (!materialId || !isCardEnabled(materialId)) {
      const first = account.formulasToAccount.find(f => isCardEnabled(f.formulaId))?.formulaId;
      if (first) setMaterialId(first);
    }
  }, [account.formulasToAccount, materialId, isCardEnabled, setMaterialId]);

  React.useEffect(() => {
    if (!account || !materialId) {
      setTerminals([]);
      return;
    }

    const accountMaterial = getAccountMaterial(account);
    const terminalsValue = materialId === GENERAL_ID ? [] : (accountMaterial?.terminals || []);
    setTerminals(terminalsValue);
    
    // Reset time granularity to Month when switching materials
    setTimeGranularity('Month');
    setLogisticsTimeGranularity('Month');
  }, [account, materialId]);

  // Compute disabled formula codes for shared card bar
  const disabledFormulaCodes = React.useMemo(() => 
    account.formulasToAccount
      .filter(f => !isCardEnabled(f.formulaId))
      .map(f => f.formulaId),
    [account.formulasToAccount, isCardEnabled],
  );

  // Configure shared formula card bar — hidden on the full-width Commodex table view.
  useCardBar({ visible: activeSub !== 'commodex-table' && activeSub !== 'terminal-coverage', includeGeneral: activeSub === 'exposure' || activeSub === 'm2m', disabledFormulaCodes });

  const handleTogglePolicyVisibility = React.useCallback(async (policyKey: string) => {
    if (!accountId) return;
    const current = account.visiblePolicies;
    // If undefined (all visible), initialize with all keys then remove the toggled one
    let updated: string[];
    if (!current) {
      updated = subMenuItems.map(i => i.key).filter(k => k !== policyKey);
    } else if (current.includes(policyKey)) {
      updated = current.filter(k => k !== policyKey);
    } else {
      updated = [...current, policyKey];
    }

    // Optimistic local update
    useAccountStore.setState({
      account: { ...account, visiblePolicies: updated },
    });

    // Persist to Postgres
    try {
      const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) return;
      const token = await firebaseUser.getIdToken();
      await fetch(`${API_URL}/api/v2/accounts/${accountId}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ settings: { visiblePolicies: updated } }),
      });
    } catch (error) {
      console.error('[Strategy] Failed to save policy visibility:', error);
    }
  }, [account, accountId]);

  // Pivot table for admin debug tab
  React.useEffect(() => {
    if (logisticsData.length > 0) {
      const cells = Object.keys(logisticsData[0] || {});
      const pivoted: any[] = cells.map(() => ({}));
      const cellStr = 'abcdefghijklmnopqrstuvwxyz';

      logisticsData.forEach((item, i) => {
        cells.forEach((cell, ci) => {
          pivoted[ci]['a'] = cell;
          pivoted[ci][cellStr[i + 1]] = item[cell as keyof LogisticsMonthData];
        });
      });
      setLogisticsPivot(pivoted);
    }
  }, [logisticsData]);

  return materialId ? (
    <div className="flex flex-col gap-3 h-full w-full">
      <div className="flex gap-3 flex-1 overflow-hidden">
        {/* Left sidebar: Policy tabs */}
        <SubMenu
          items={subMenuItems}
          active={activeSub}
          onChange={setActiveSub}
          isSuperAdmin={isSuperAdminUser}
          visiblePolicies={account.visiblePolicies}
          onToggleVisibility={handleTogglePolicyVisibility}
        />

        {/* Main content: 3-area layout */}
        <div className="flex flex-col gap-3 flex-1 overflow-hidden">
          {/* Area 1: Main chart (full width) */}
          <div className="flex-1 min-h-0 overflow-auto">
            {account && activeSub === 'buffer' && (
              <BufferManagement
                materialName={materialsMap[materialId]?.name || ''}
                formulaId={materialId}
                buffer={policyConfig?.buffer ?? bufferDefault}
                bufferData={bufferData}
                matPrice={pricesMap[materialId] || 0}
                unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                onTimeChange={(time) => setTimeGranularity(time)}
                account={account}
                terminals={terminals}
                onMonthSelect={(index) => setSelectedMonthIndex(index)}
                selectedIndex={selectedMonthIndex}
              />
            )}
            {activeSub === 'logistics' && (
              <LogisticsInvManagement
                materialName={materialsMap[materialId]?.name || ''}
                formulaId={materialId}
                logisticsData={logisticsData}
                matPrice={pricesMap[materialId] || 0}
                unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                onTimeChange={(time) => setLogisticsTimeGranularity(time)}
                terminals={terminals}
                onMonthSelect={(index) => setSelectedMonthIndex(index)}
                selectedIndex={selectedMonthIndex}
              />
            )}
            {activeSub === 'coverage' && (
              <CoverageManagement
                key={`coverage-${materialId}`}
                materialName={materialsMap[materialId]?.name || ''}
                formulaId={materialId}
                coverageData={coverageData}
                matPrice={pricesMap[materialId] || 0}
                unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                onTimeChange={(time) => setLogisticsTimeGranularity(time)}
                terminals={terminals}
                onMonthSelect={(index) => setSelectedMonthIndex(index)}
                selectedIndex={selectedMonthIndex}
              />
            )}
            {activeSub === 'hedges-deriving' && account && accountId && (
              <HedgesDeriving
                accountId={typeof accountId === 'string' ? parseInt(accountId) : accountId}
                baseMaterialId={materialId}
                baseMaterialPrice={pricesMap[materialId] || 0}
                unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                terminals={terminals}
                onMonthSelect={(index) => setSelectedMonthIndex(index)}
                selectedIndex={selectedMonthIndex}
                onTimeChange={(time) => setLogisticsTimeGranularity(time)}
                timeGranularity={logisticsTimeGranularity}
                onDataCalculated={(data) => {
                  setHedgesDerivedData(data.chartData);
                  setDerivedMaterials(data.derivedMaterials);
                  setFormulaColorDictionary(data.formulaColorDictionary);
                }}
              />
            )}
            {activeSub === 'base-material' && account && accountId && (
              <BaseMaterialManagement
                accountId={typeof accountId === 'string' ? parseInt(accountId) : accountId}
                baseMaterialId={materialId}
                baseMaterialPrice={pricesMap[materialId] || 0}
                unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                terminals={terminals}
                timeGranularity={logisticsTimeGranularity}
                onMonthSelect={(index) => setSelectedMonthIndex(index)}
                selectedIndex={selectedMonthIndex}
                onTimeChange={(time) => setLogisticsTimeGranularity(time)}
                onDataCalculated={(data) => {
                  setBaseMaterialData(data.chartData);
                  setBaseDerivedMaterials(data.derivedMaterials);
                }}
              />
            )}
            {activeSub === 'terminal-coverage' && account && accountId && materialId && (
              <TerminalCoverageTable
                accountId={typeof accountId === 'string' ? parseInt(accountId) : accountId}
                baseMaterialId={materialId}
              />
            )}
            {activeSub === 'commodex-table' && account && accountId && materialId && (
              <CommodexTable
                accountId={typeof accountId === 'string' ? parseInt(accountId) : accountId}
                baseMaterialId={materialId}
              />
            )}
            {activeSub === 'hedging-recommendations' && account && accountId && (
              <HedgingRecommendations
                accountId={typeof accountId === 'string' ? parseInt(accountId) : accountId}
                formulaId={numericFormulaId}
              />
            )}
            {activeSub === 'hedge-price' && materialId && (
              <HedgePriceManagement
                formulaId={materialId}
                materialName={materialsMap[materialId]?.name || ''}
                supplyPositions={supplyPositions}
                demandPositions={demandPositions}
                hedgePositions={hedgePositions}
                spotPrice={pricesMap[materialId] || 0}
                terminals={terminals}
                onBarSelect={(index) => setSelectedMonthIndex(index)}
                selectedIndex={selectedMonthIndex}
                timeGranularity={priceTimeGranularity}
                onTimeChange={setPriceTimeGranularity}
                driftThreshold={policyConfig?.driftThreshold}
              />
            )}
            {activeSub === 'exposure' && (
              <ExposureChart
                exposureData={exposureHistory}
                formulaId={materialId}
                exposureThresholds={policyConfig?.exposureThresholds}
                selectedSnapshot={selectedExposureSnapshot}
                onPointClick={setSelectedExposureSnapshot}
              />
            )}
            {activeSub === 'm2m' && (
              <M2mManagement
                formulaId={materialId}
                materialName={materialsMap[materialId]?.name || ''}
                snapshot={m2mSnapshot || null}
                isLoading={isM2mLoading}
                colorsMap={colorsMap}
                onCommoditySelect={(fc) => setMaterialId(fc)}
                threshold={policyConfig?.m2mThresholds.marginExcessMin}
              />
            )}
            {activeSub === 'admin-inv' && (
              <div className="text-xs overflow-auto p-4">
                <AdminBufferTable rows={logisticsData} />
              </div>
            )}
            {activeSub === 'admin-pivot' && (
              <div className="text-xs overflow-auto p-4">
                <AdminBufferTable rows={logisticsPivot} />
              </div>
            )}
          </div>

          {/* Bottom: Area 2 (Settings) + Area 3 (Detail) side by side — hidden for full-table views */}
          <div className={`flex gap-3 h-2/5 min-h-[200px] ${activeSub === 'terminal-coverage' || activeSub === 'commodex-table' || activeSub === 'hedging-recommendations' ? 'hidden' : ''}`}>
            {/* Area 2: Settings & notifications */}
            <div className="flex flex-col w-1/2 gap-3 overflow-hidden">
              <StrategyDetails
                {...{ formulaId: materialId, startDate, colorsMap, account, exposureHistory }}
                accountMaterial={getAccountMaterial(account)}
                activeSub={activeSub}
                accountId={accountId}
                selectedMonthIndex={selectedMonthIndex}
                latestSnapshot={latestSnapshot}
                onRecalculated={() => { refetchHistory(); refetchLatest(); }}
                headerAction={undefined}
                watchRule={activeWatchRule}
                watchRuleMeta={activeRuleMeta}
                allWatchRules={allActiveRules}
                allRuleTypes={ruleTypes}
                onWatchRuleSave={handleWatchRuleSave}
                isWatchRuleSaving={updateWatchRuleMutation.isLoading}
                onCreateRule={handleCreateRule}
                isCreatingRule={createWatchRuleMutation.isLoading}
              />
            </div>

            {/* Area 3: Detail view for selected bar/month */}
            <div className="flex w-1/2 overflow-hidden">
              {account && activeSub === 'exposure' && (
                <ExposureSummary
                  {...{ formulaId: materialId, account, netExposure, materialsMap }}
                  accountMaterial={getAccountMaterial(account)}
                  exposureThresholds={policyConfig?.exposureThresholds}
                  latestSnapshot={latestSnapshot}
                  selectedSnapshot={selectedExposureSnapshot ?? undefined}
                />
              )}
              {account && activeSub === 'buffer' && (
                <BufferMonthDetail
                  bufferData={bufferData}
                  matPrice={pricesMap[materialId] || 0}
                  unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                  selectedMonthIndex={selectedMonthIndex}
                  timeGranularity={timeGranularity}
                />
              )}
              {account && activeSub === 'm2m' && (
                (materialId === GENERAL_ID || !materialId)
                  ? <M2mAccountDetail snapshot={m2mSnapshot || null} threshold={policyConfig?.m2mThresholds.marginExcessMin} />
                  : <M2mDetail snapshot={m2mSnapshot || null} formulaId={materialId} />
              )}
              {account && activeSub === 'logistics' && (
                <LogisticsMonthDetail
                  logisticsData={logisticsData}
                  matPrice={pricesMap[materialId] || 0}
                  unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                  selectedMonthIndex={selectedMonthIndex}
                  timeGranularity={logisticsTimeGranularity}
                />
              )}
              {account && activeSub === 'coverage' && (
                <CoverageMonthDetail
                  coverageData={coverageData}
                  matPrice={pricesMap[materialId] || 0}
                  unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                  selectedMonthIndex={selectedMonthIndex}
                  timeGranularity={logisticsTimeGranularity}
                />
              )}
              {account && activeSub === 'hedges-deriving' && baseHedgeMaterials && baseHedgeMaterials.length > 0 && (
                <HedgesDerivedMonthDetail
                  hedgesDerivedData={hedgesDerivedData}
                  selectedMonthIndex={selectedMonthIndex}
                  derivedMaterials={derivedMaterials || []}
                  formulaColorDictionary={formulaColorDictionary}
                  timeGranularity={logisticsTimeGranularity}
                />
              )}
              {account && activeSub === 'base-material' && baseMaterialData.length > 0 && (
                <BaseMaterialMonthDetail
                  data={baseMaterialData}
                  matPrice={pricesMap[materialId] || 0}
                  selectedMonthIndex={selectedMonthIndex}
                  derivedMaterials={baseDerivedMaterials}
                  formulaColorDictionary={colorsMap}
                  timeGranularity={logisticsTimeGranularity}
                />
              )}
              {account && activeSub === 'hedge-price' && materialId && (
                <HedgePriceMonthDetail
                  buckets={calculatePriceInsightData(
                    materialId,
                    supplyPositions,
                    demandPositions,
                    hedgePositions,
                    pricesMap[materialId] || 0,
                    priceTimeGranularity,
                    terminals,
                  )}
                  selectedIndex={selectedMonthIndex}
                  timeGranularity={priceTimeGranularity}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {(loading || isDemandLoading || isSupplyLoading || (activeSub === 'exposure' && isLoadingHistory)) && (
        <LoadingSpinnerOverlay />
      )}

    </div>
  ) : null;
};

export default Strategies;
